import { apiRequest } from "src/utils/apiRequest";

export interface Evidence {
    evidenceId: string;
    uri: string;
    fileType: string;
    presignedUrl?: string; // Short-lived pre-signed s3 URL returned by the backend for direct consumption
}

export interface ObjectPresenceCheck {
    object_name: string;
    is_compliant: boolean;
    ai_reason: string;
    overriddenEvidences?: Evidence[];
    originalIsCompliant?: boolean;
}

export interface InspectionResult {
    resultId: string;
    questionId: string;
    objectId: string | null;
    aiResult: string;
    aiReasoning: string;
    confirmedResult: string;
    isCompliant: boolean;
    isInspectorOverride: boolean;
    overrideReason: string | null;
    /** Optional free-text note the inspector entered when answering a manual question. */
    userComment?: string | null;
    evidence: Evidence[];
    overriddenEvidences?: Evidence[];
    originalIsCompliant?: boolean;
    objectPresenceChecks?: ObjectPresenceCheck[];
}

export interface InspectionData {
    inspectionId: string;
    austinInspectionId?: string; // Present only when linked to an Austin scheduled inspection
    templateId: string;
    templateName: string;
    siteCode: string;
    createdBy: string;
    createdAt: string;
    completedAt: string | null;
    status: string;
    results: InspectionResult[];
}

export interface InspectionWithPresignedUrls {
    inspectionData: InspectionData;
    presignedUrlMap: Record<string, string>;
    groupedResults: Record<string, InspectionResult[]>;
}

export type InspectionStatus = "In_Progress" | "Completed";

export type UpdateInspectionPayload = {
  inspectionId: string;
  templateId: string;
  templateName: string;
  siteCode: string;
  status: InspectionStatus;
  createdBy: string;
  createdAt: string;
};

/**
 * Shared helper to determine whether an item has been manually overridden.
 * Works with both entity types in the data model:
 *  - InspectionResult (drum questions): uses `isInspectorOverride`
 *  - ObjectPresenceCheck (object-level): uses `overriddenEvidences.length > 0`
 */
export function isOverridden(item: InspectionResult | ObjectPresenceCheck): boolean {
    if ("isInspectorOverride" in item) {
        return item.isInspectorOverride;
    }
    return (item.overriddenEvidences?.length ?? 0) > 0;
}

/**
 * Placeholder values written to `overrideReason` when the inspector supplied no comment:
 * the client sends "manual_override" and the backend falls back to "Manually overridden".
 * Neither is inspector-authored text, so neither should surface as one.
 */
const OVERRIDE_REASON_SENTINELS = ["manual_override", "manually overridden"];

/**
 * Returns the inspector's override justification, or null when `overrideReason` holds
 * only a machine-written placeholder.
 */
export function getOverrideComment(overrideReason: string | null | undefined): string | null {
    const reason = overrideReason?.trim();
    if (!reason || OVERRIDE_REASON_SENTINELS.includes(reason.toLowerCase())) {
        return null;
    }
    return reason;
}

/**
 * Resolves which comment a detail sheet should display, based on which tab/state it is
 * currently presenting. Shared by ComplianceDetailSheet (InspectionResult) and
 * ObjectPresenceDetailSheet (ObjectPresenceCheck), which otherwise duplicate this exact
 * branch against their own field names.
 *
 * - Viewing the "Overridden" state (or a result with no tabs at all, still the overridden
 *   view): show the override justification, falling back to the original comment when the
 *   override was submitted without one.
 * - Viewing the "Original result" tab, or the item was never overridden: show the original
 *   answer-time comment.
 */
export function resolveDisplayedComment(
    isItemOverridden: boolean,
    isOriginalTabActive: boolean,
    overrideReason: string | null | undefined,
    userComment: string | null | undefined
): string | null | undefined {
    const isViewingOverride = isItemOverridden && !isOriginalTabActive;
    if (!isViewingOverride) {
        return userComment;
    }
    return getOverrideComment(overrideReason) ?? userComment;
}

class InspectionService {
    /**
     * Triggers the backend auto-calculation for a given question within an
     * inspection. The backend computes the result automatically (no user input
     * required) and returns the resulting InspectionResult.
     */
    async autoCalculate(inspectionId: string, questionId: string): Promise<InspectionResult> {
        const data = await apiRequest<InspectionResult>({
            url: `/inspections/auto-calculate`,
            method: "POST",
            data: {
                inspectionId: inspectionId,
                questionId: questionId,
            },
        });
        return data;
    }

    async completeInspection(payload: UpdateInspectionPayload) {
        const data = await apiRequest({
            url: `/inspections`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    /**
     * Complete an inspection and submit it. The backend marks the inspection
     * COMPLETED and, if it is linked to an Austin scheduled inspection, starts
     * and submits it to Austin/RCMS. Returns the completed inspection.
     */
    async submitInspection(inspectionId: string): Promise<InspectionData> {
        const data = await apiRequest({
            url: `/inspections/submit`,
            method: "POST",
            data: { inspectionId },
        });
        return data;
    }

    async recordManualResult(payload: FormData) {
        const data = await apiRequest({
            url: `/inspections/record-manual-result`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    async fetchInspectionWithPresignedUrls(
        inspectionId: string
    ): Promise<InspectionWithPresignedUrls> {
        const data = await apiRequest({
            url: `/inspections/${inspectionId}`,
            method: "GET",
        });

        // The backend returns a presigned s3 URL on each evidence item, so no
        // separate presigning call is needed. Build a S3-URI -> presigned-URL map
        // from the payload to keep the shape the review components already consume.
        const allEvidence = (data.results ?? []).flatMap((result: InspectionResult) => [
            ...(result.evidence ?? []),
            ...(result.overriddenEvidences ?? []),
            ...(result.objectPresenceChecks ?? []).flatMap((check: ObjectPresenceCheck) =>
                check.overriddenEvidences ?? []
            ),
        ]);

        const presignedUrlMap: Record<string, string> = {};
        for (const ev of allEvidence) {
            if (ev.uri?.startsWith('s3://') && ev.presignedUrl) {
                presignedUrlMap[ev.uri] = ev.presignedUrl;
            }
        }

        // Group results by questionId
        const groupedResults: Record<string, InspectionResult[]> = {};
        data.results?.forEach((result: InspectionResult) => {
            if (!groupedResults[result.questionId]) {
                groupedResults[result.questionId] = [];
            }
            groupedResults[result.questionId].push(result);
        });

        return {
            inspectionData: data,
            presignedUrlMap,
            groupedResults,
        };
    }
}

export default new InspectionService();
